import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Send, StickyNote, User, DollarSign, Copy } from 'lucide-react';
import type { AdminOutletContext, Client, QuoteRecord } from '../types';
import { MOCK_CLIENTS } from '../types';
import MissingAgentNotice from '../components/MissingAgentNotice';
import { useAgent } from '../context/AgentsContext';
import FlightQuoteModal from '../components/FlightQuoteModal';
import { passengersLabel } from '../lib/passengers';
import { syncAdminFromServer } from '../lib/syncAdminFromServer';
import {
  appendActivity,
  appendEmailLog,
  appendNotification,
  getActivitiesForClient,
  getQuotesForClient,
  loadCms,
  loadClients,
  saveClients,
} from '../lib/skyluxStore';

const AdminClientDetail = () => {
  const { agentId, clientId } = useParams();
  const navigate = useNavigate();
  const { globalSearch } = useOutletContext<AdminOutletContext>();
  const agent = useAgent(agentId);

  const [clients, setClients] = useState<Client[]>(() => loadClients(MOCK_CLIENTS));
  const [tick, setTick] = useState(0);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const refreshFromServer = useCallback(() => {
    void syncAdminFromServer(MOCK_CLIENTS).then(() => {
      setClients(loadClients(MOCK_CLIENTS));
      setTick((t) => t + 1);
    });
  }, []);

  useEffect(() => {
    refreshFromServer();
  }, [refreshFromServer]);

  useEffect(() => {
    const fn = () => {
      setClients(loadClients(MOCK_CLIENTS));
      setTick((t) => t + 1);
    };
    window.addEventListener('skylux-storage', fn);
    const onFocus = () => refreshFromServer();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('skylux-storage', fn);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshFromServer]);

  const client = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);

  useEffect(() => {
    if (client) setNoteDraft(client.notes ?? '');
  }, [client]);

  const quotes = useMemo(() => {
    void tick;
    return clientId ? getQuotesForClient(clientId) : [];
  }, [clientId, tick]);

  const activities = useMemo(() => {
    void tick;
    const rows = clientId ? getActivitiesForClient(clientId) : [];
    const q = globalSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((a) => a.title.toLowerCase().includes(q) || a.detail.toLowerCase().includes(q));
  }, [clientId, tick, globalSearch]);

  const upsertClient = (c: Client) => {
    setClients((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id);
      const next = idx >= 0 ? prev.map((x) => (x.id === c.id ? c : x)) : [c, ...prev];
      saveClients(next);
      return next;
    });
  };

  const saveNotes = () => {
    if (!client) return;
    const next = { ...client, notes: noteDraft };
    upsertClient(next);
    appendActivity({
      id: `act_${Date.now()}`,
      clientId: client.id,
      at: new Date().toISOString(),
      type: 'note',
      title: 'Notes updated',
      detail: noteDraft.slice(0, 200) + (noteDraft.length > 200 ? '…' : ''),
    });
  };

  const setStatus = (status: Client['status']) => {
    if (!client) return;
    upsertClient({ ...client, status });
    appendActivity({
      id: `act_${Date.now()}`,
      clientId: client.id,
      at: new Date().toISOString(),
      type: 'status_changed',
      title: 'Status updated',
      detail: `Set to ${status}`,
    });
  };

  const sendWelcomeEmail = () => {
    if (!agent || !client) return;
    if (!client.email.trim()) {
      window.alert('Add an email on the profile first.');
      return;
    }
    const cms = loadCms();
    appendEmailLog({
      id: `em_${Date.now()}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: agent.email,
      to: client.email,
      subject: 'Welcome to Skylux Travel',
      kind: 'welcome',
      preview: `${cms.landingHeroTitle}\n\n${cms.landingHeroSubtitle}`,
    });
    appendActivity({
      id: `act_${Date.now()}`,
      clientId: client.id,
      at: new Date().toISOString(),
      type: 'email_test',
      title: 'Welcome email queued',
      detail: `To ${client.email} (demo)`,
    });
    appendNotification({
      id: `n_${Date.now()}`,
      type: 'email',
      title: 'Welcome email',
      message: `Sent to ${client.name}`,
      at: new Date().toISOString(),
      read: false,
    });
    window.alert('Welcome email logged (demo).');
  };

  const sendPaymentReminder = (q: QuoteRecord) => {
    if (!agent) return;
    if (!client?.email.trim()) {
      window.alert('Client needs an email.');
      return;
    }
    if (q.paymentStatus === 'paid') {
      window.alert('This quote is already paid.');
      return;
    }
    const cms = loadCms();
    const preview = `${cms.paymentReminderBody}\n\nPay: ${q.paymentUrl}\nAmount: $${q.pricePerPerson}`;
    appendEmailLog({
      id: `em_${Date.now()}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: agent.email,
      to: client.email,
      subject: `Reminder: complete payment — ${q.flight.departure} to ${q.flight.arrival}`,
      kind: 'payment_reminder',
      preview,
    });
    appendActivity({
      id: `act_${Date.now()}`,
      clientId: client.id,
      at: new Date().toISOString(),
      type: 'payment_reminder',
      title: 'Payment reminder sent',
      detail: q.id,
    });
    appendNotification({
      id: `n_${Date.now()}`,
      type: 'reminder',
      title: 'Payment reminder',
      message: `${client.name} — quote ${q.id.slice(0, 8)}…`,
      at: new Date().toISOString(),
      read: false,
    });
    window.alert('Payment reminder logged (demo).');
  };

  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    window.alert('Copied to clipboard.');
  };

  if (!agent) {
    return (
      <div className="animate-fade" style={{ padding: '2rem' }}>
        <MissingAgentNotice />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="animate-fade" style={{ padding: '2rem' }}>
        <p style={{ color: 'var(--text-muted)' }}>Client not found.</p>
        <Link to={`/admin/agent/${agentId}/clients`} style={{ color: 'var(--accent)', fontWeight: '600' }}>
          ← Back to directory
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: '2rem' }}>
        <button type="button" onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'none', border: 'none', color: 'var(--accent)', fontWeight: '600', marginBottom: '1rem', cursor: 'pointer' }}>
          <ArrowLeft size={18} /> Back
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '2rem', fontWeight: '800' }}>{client.name}</h2>
            <p style={{ color: 'var(--text-muted)' }}>
              {client.email || 'No email'} · {client.phone ?? '—'} · {client.source ?? 'lead'}
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <button type="button" onClick={() => setQuoteOpen(true)} style={{ padding: '0.65rem 1.1rem', borderRadius: '0.75rem', background: 'var(--primary)', color: 'white', fontWeight: '700' }}>
              <Send size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Search & quote
            </button>
            <button type="button" onClick={sendWelcomeEmail} style={{ padding: '0.65rem 1.1rem', borderRadius: '0.75rem', background: '#eff6ff', color: 'var(--accent)', fontWeight: '700' }}>
              <Mail size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Welcome email
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ background: 'white', borderRadius: '1.25rem', border: '1px solid var(--border)', padding: '1.5rem' }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <User size={18} /> Profile & trip request
          </h3>
          <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.9rem' }}>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Route: </span>
              <strong>
                {client.origin ?? '—'} → {client.destination ?? '—'}
              </strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Travel date: </span>
              <strong>{client.travelDate ?? '—'}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Status: </span>
              <strong>{client.status}</strong>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button type="button" onClick={() => setStatus('pending')} style={{ padding: '0.35rem 0.75rem', borderRadius: '0.5rem', background: '#fffbeb', fontSize: '0.75rem', fontWeight: '700' }}>
                Pending
              </button>
              <button type="button" onClick={() => setStatus('quoted')} style={{ padding: '0.35rem 0.75rem', borderRadius: '0.5rem', background: '#eff6ff', fontSize: '0.75rem', fontWeight: '700' }}>
                Quoted
              </button>
              <button type="button" onClick={() => setStatus('paid')} style={{ padding: '0.35rem 0.75rem', borderRadius: '0.5rem', background: '#ecfdf5', fontSize: '0.75rem', fontWeight: '700' }}>
                Paid
              </button>
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '1.25rem', border: '1px solid var(--border)', padding: '1.5rem' }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <StickyNote size={18} /> Internal notes
          </h3>
          <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={5} placeholder="Call preferences, budget, companions…" style={{ width: '100%', marginBottom: '0.75rem' }} />
          <button type="button" onClick={saveNotes} style={{ padding: '0.5rem 1.25rem', borderRadius: '0.65rem', background: 'var(--accent)', color: 'white', fontWeight: '700' }}>
            Save notes
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }} className="client-detail-grid">
        <div style={{ background: 'white', borderRadius: '1.25rem', border: '1px solid var(--border)', padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <DollarSign size={20} /> Quotes & assignments
          </h3>
          {quotes.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No quotes yet. Use “Search & quote” to assign a flight and email the client.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '1rem' }}>
              {quotes.map((q) => (
                <li key={q.id} style={{ border: '1px solid var(--border)', borderRadius: '0.85rem', padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <strong>
                      {q.flight.airline} {q.flight.flightNumber}
                    </strong>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: q.paymentStatus === 'paid' ? 'var(--success)' : '#b45309' }}>{q.paymentStatus === 'paid' ? 'PAID' : 'UNPAID'}</span>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>
                    {q.flight.departure} → {q.flight.arrival} · ${q.pricePerPerson} pp
                  </p>
                  <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', lineHeight: 1.45 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Travelers: </span>
                    {q.passengers?.length ? (
                      <strong>{passengersLabel(q, '; ')}</strong>
                    ) : (
                      <span style={{ color: '#b45309' }}>Not completed on booking yet</span>
                    )}
                    {q.bookingUpdatedAt ? (
                      <span style={{ display: 'block', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                        Booking details saved {new Date(q.bookingUpdatedAt).toLocaleString()}
                      </span>
                    ) : null}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button type="button" onClick={() => copy(q.bookingUrl)} style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem', borderRadius: '0.45rem', background: '#f1f5f9', fontWeight: '600' }}>
                      <Copy size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Booking link
                    </button>
                    <button type="button" onClick={() => copy(q.paymentUrl)} style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem', borderRadius: '0.45rem', background: '#f1f5f9', fontWeight: '600' }}>
                      Payment link
                    </button>
                    {q.paymentStatus === 'unpaid' && (
                      <button type="button" onClick={() => sendPaymentReminder(q)} style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem', borderRadius: '0.45rem', background: '#fff7ed', fontWeight: '700', color: '#9a3412' }}>
                        Payment reminder
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: '1.25rem', border: '1px solid var(--border)', padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: '800', marginBottom: '1rem' }}>Activity timeline</h3>
          {activities.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No activity yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.85rem', maxHeight: '420px', overflowY: 'auto' }}>
              {activities.map((a) => (
                <li key={a.id} style={{ borderLeft: '3px solid var(--accent)', paddingLeft: '0.75rem' }}>
                  <p style={{ fontWeight: '700', fontSize: '0.85rem' }}>{a.title}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{new Date(a.at).toLocaleString()}</p>
                  <p style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>{a.detail}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .client-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <FlightQuoteModal
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        agent={agent}
        client={client}
        onUpsertClient={upsertClient}
        onAfterQuoteSent={() => refreshFromServer()}
      />
    </div>
  );
};

export default AdminClientDetail;
